/* eslint-disable */
import Map from "../map";
import {
  loadCSV,
  loadJSON,
  loadMapbox,
  setupResizeHandler,
  // getStripes,
  makeid
} from "../utils";
import { getColors } from "../colors";
import { Logger } from "../logger";
import iframeDetect from "../iframe-detect";
import { translate, formatFloat } from "../language";
import { scaleThreshold, scaleLinear } from "d3-scale";
import centerOfMass from "@turf/center-of-mass";
import ColorsLegend from "../colorslegend";
import ToggleButtons from '../togglebuttons';

const t = translate;
const log = new Logger("tsg_chart_03_map_early_ed_share");

const template = `
  <div class="tsg-03_map_early_ed_share">
    <div class="tsg-03_map_early_ed_share__toggle"></div>
    <div class="tsg-03_map_early_ed_share__ui tsg-ui tsg-ui-legend-search">
      <div class="tsg-03_map_early_ed_share__legend"></div>
    </div>
    <div class="tsg-03_map_early_ed_share__map-container">
      <div class="tsg-03_map_early_ed_share__map"></div>
      <div class="tsg-03_map_early_ed_share__mlegend tsg-map-legend"></div>
    </div>
  </div>
`;

export default function chart(container) {
  if (!container) {
    log.error("container does not exist");
    return Promise.resolve();
  }

  const colors = getColors();

  return Promise.all([
    loadMapbox(), // 0
    loadJSON(
      `${PATH}data/03_map_early_ed_share/europe.sim.geo.json`
    ), // 1
    loadCSV(`${PATH}data/03_map_early_ed_share/03_map_early_ed_share.csv`),
    iframeDetect.checkFixedHeight()
  ]).then(res => {
    container.innerHTML = template;
    const containerMap = container.querySelector(
      ".tsg-03_map_early_ed_share__map-container"
    );
    const containerMapMap = container.querySelector(
      ".tsg-03_map_early_ed_share__map"
    );
    const containerLegend = container.querySelector(
      ".tsg-03_map_early_ed_share__legend"
    );
    const containerToggle = container.querySelector(
      ".tsg-03_map_early_ed_share__toggle"
    );
    containerMap.style.position = "relative";

    const ar = 1.5;
    const minHeight = 500;
    if (res[4]) {
      // TODO: this is not the correct thing
      containerMap.style.height = `${iframeDetect.height}px`;
    } else {
      const doResize = () => {
        if (containerMap.getBoundingClientRect().width > minHeight * ar) {
          containerMap.style.height = "0";
          containerMap.style.paddingBottom = `${(1 / ar) * 100}%`;
        } else {
          containerMap.style.height = `${minHeight}px`;
          containerMap.style.paddingBottom = "0";
        }
      };
      doResize();
      window.addEventListener("resize", doResize);
    }

    const formatFunction = formatFloat({
      digits: 1,
      postfix: "%",
      adaptive: true,
    });

    const formatFunctionL = formatFloat({
      digits: 1,
      postfix: "%",
      adaptive: true,
      decimal: true,
    });

    const geoData = res[1];
    const dataLookup = res[2];
    const geoIdentifier = "ISO3";
    const countryIdentifier = "ISO3";
    const colorRange = colors.mapScale1;

    const toggleOptions = {
      "age_under_2": {
        domain: [0, 20, 40, 60, 80, 100],
        range: colorRange,
        label: t("under 2 years")
      },
      "age_2": {
        domain: [0, 20, 40, 60, 80, 100],
        range: colorRange,
        label: t("2-year-olds")
      },
      "age_3": {
        domain: [0, 20, 40, 60, 80, 100],
        range: colorRange,
        label: t("3-year-olds")
      },
      "age_4": {
        domain: [0, 20, 40, 60, 80, 100],
        range: colorRange,
        label: t("4-year-olds")
      },
    }

    let selected = "age_under_2";

    const choroplethColors = {
      min: colors.primary,
      max: colors.secondary
    };

    const calcCenter = f => {
      const center = centerOfMass(f).geometry.coordinates;
      f.properties.centerLng = center[0];
      f.properties.centerLat = center[1];
    };

    geoData.features.forEach(f => {
      const countryIso = f.properties[countryIdentifier];
      const dataObj = dataLookup.find(({ country_iso }) => country_iso === countryIso);
      if (dataObj) {
        Object.keys(toggleOptions).forEach(key => {
          f.properties[key] = dataObj[key];
        });
        f.properties.NAME = t(dataObj.country);//t(f.properties.NAME);
      } else {
        f.properties.filter = true;
      }
      calcCenter(f);
    });
    geoData.features = geoData.features.filter(f => !f.properties.filter);

    const colorData = {};

    Object.entries(toggleOptions).forEach(([key, { domain, range }]) => {
      colorData[key] = {};
      const colorScale = scaleLinear()
        .domain(domain)
        .range(range)

      geoData.features.forEach(f => {
        const geoId = f.properties[geoIdentifier];
        if (f.properties[key]) colorData[key][geoId] = colorScale(f.properties[key]);
      });
    })

    let popup;
    let hoveredRegion;
    const removePopup = () => {
      if (popup) popup.remove();
    };

    const addPopup = (feature, lngLat) => {
      const props = feature.properties ? feature.properties : feature;
      const value = props[selected];

      if (popup) popup.remove();

      let html = "";
      html = `
        <div class="tsg-map-popup__heading">${props.NAME}</div>
        <div class="tsg-map-popup-item">${
          !!value
            ? formatFunction(value)
            : t("no data")
        }</div>
      `;

      popup = new window.mapboxgl.Popup({
        color: "#1f1f1f",
        scale: 0.7,
        closeButton: false,
        closeOnClick: false,
        closeOnMove: false,
        className: "region-popup"
      })
        .setLngLat(lngLat || [props.centerLng, props.centerLat])
        .setHTML(html)
        .addTo(map.map);
    };

    const generateLabels = () => {
      const labels = { type: "FeatureCollection", features: []};
      dataLookup.forEach((d) => {
        labels.features.push({
          type: "Point",
          geometry: { type: "Point", coordinates: [d.label_lon, d.label_lat]},
          properties: {
            prio: +d.label_prio,
            label: t(d.country) + '\n' + formatFunctionL(d[selected]),
            offset: [
              d.label_offset_x ? +d.label_offset_x : 0,
              d.label_offset_y ? +d.label_offset_y : 0,
            ]
          }
        });
      });

      return labels;
    }

    const map = new Map(containerMapMap, {
      initialBounds: [
        [44.13903192734202, 70.84741049738273],
        [-25.635407958361014, 27.12957487411873],
      ],
      minZoom: 1.3,
      maxZoom: 5,
      maxBounds: [
        [-59.7479938204966, 20.683255614394483],
        [79.71095132640886, 74.4596189280769]
      ],
      labels: true,
      controls: true,
      popup: true,
      popupShift: true,
      style: `${PATH}data/03_map_early_ed_share/style.json`,
    });

    map.activated
      .then(() => map.loaded)
      .then(() => {
        const REFLAYER = "countries";

        const mousemove = (f, filtered) => {
          // if (isTouch) return;
          hoveredRegion = f.properties;
          addPopup(f);
        };

        const mouseleave = () => {
          setTimeout(() => {
            hoveredRegion = undefined;
            removePopup();
          });
        };

        map.addChoropleth(geoData, {
          id: "nuts",
          identifier: geoIdentifier,
          geoType: "geojson",
          interactive: true,
          borderColor: "#fff",
          borderWidth: 1.5,
          hoveringBorderColor: "#000",
          hoveringBorderWidth: 3,
          dataType: "colors",
          noDataColor: "#ddd",
          colors: colorData.age_under_2,
          data: [0],
          //beforeLayer: REFLAYER,
          mousemoveCallback: mousemove,
          mouseleaveCallback: mouseleave,
          beforeLayer: 'labels',
        });

        /*
        map.map.addLayer({
          id: "countries_lines",
          source: "countries",
          "source-layer": "countries",
          type: "line",
          layout: {
            "line-cap": "round",
            "line-join": "round"
          },
          paint: {
            "line-opacity": 1.0,
            "line-width": ["step", ["zoom"], 1, 3.5, 3],
            "line-color": "#fff"
          }
        }, REFLAYER);
        */ 

        map.map.addSource('labels', {
          type: 'geojson',
          data: generateLabels(),
        });

        map.map.addLayer({
          id: 'labels',
          source: 'labels',
          type: 'symbol',
          layout: {
            'text-field': '{label}',
            'symbol-sort-key': ['get', 'prio'],
            //'text-font': ['Franklin Gothic Book'],
            'text-font': ['PT Sans Regular'],
            'text-anchor': 'center',
            'text-size': 12,
            'text-offset': ['get', 'offset'],
            'text-padding': 13,
          },
          paint: {
            'text-halo-color': '#fff',
            'text-halo-width': 1.2,
          },
        });

      });


    const legend = new ColorsLegend(containerLegend, {
      title: null,
      items: toggleOptions[selected].range.map((c, i) => ({
        color: c,
        label: formatFunctionL(toggleOptions[selected].domain[i])
      })),
      nullItem: { color: '#DDDDDD', label: t('no data') },
      type: "continuous"
    });


    const mlegend = container.querySelector(
      ".tsg-03_map_early_ed_share__mlegend"
    );

    map.popup.on("open", () => {
      if (map.popup.isMobile) mlegend.style.display = "none";
    });
    map.popup.on("close", () => {
      mlegend.style.display = "block";
    });

    const containerUI = container.querySelector(".tsg-ui");
    //containerUI.style.minHeight = "80px";
    containerUI.style.alignItems = "center";
    containerUI.style.marginBottom = "20px";
    setupResizeHandler(container, 600, mobile => {
      containerUI.classList.toggle("tsg-ui--mobile", mobile);
    });

    const updateLabels = () => {
      const source = map.map.getSource("labels")
      const newLabels = generateLabels();
      if (source) source.setData(newLabels);
    }

    const toggleButtonOptions = Object.entries(toggleOptions).map(([key, { label }]) => (
      { label, value: key, active: key === selected }
    ));

    new ToggleButtons(containerToggle, toggleButtonOptions, (v) => {
      selected = v;
      map.setChoroplethData("nuts", geoData, { colors: colorData[v] })
      legend.updateItems(
        toggleOptions[selected].range.map((c, i) => ({
          color: c,
          label: formatFunctionL(toggleOptions[selected].domain[i])
        }))
      )
      updateLabels();
    });

    return map;
  });
}
